import { useTranslation } from "react-i18next";
import { ChevronLeft, Lock } from "lucide-react";
import type { ManualLockHolder } from "@arc/contracts";
import { Button } from "@/components/ui/button";

/**
 * "This manual is being edited by {name}" — entry is refused (ADR-026 Phase 4).
 *
 * **A full stop, not a read-only editor.** Opening the authoring UI in a disabled state invites an author to start
 * typing into fields that will never save; the legacy showed exactly this dialog and aborted
 * (`ARLMS_ManualView.js` — DisplayManual checks the lock, shows "being edited by {username}", and returns).
 *
 * This is the ONE place the holder's name legitimately appears: reaching it means the caller passed the manual's own
 * edit gate, which is precisely the check the card badge cannot make (the list DTO is deliberately identity-free).
 *
 * **No "Request to edit" button yet — deliberately (diff-panel R9-F5).** The hand-over QUEUE is shipped and tested on
 * the server (`request-join` + the atomic `handover`, `waiting_user_id`, the idle rule), but its UI is a separate slice
 * pending product answers, so nothing in `apps/web` calls those endpoints. `waiting` is rendered here so the state is at
 * least visible when an API client queues. See ADR-026 § "Deferred slice — the request-to-join web UI".
 */
export function ManualLockedNotice({
  holderName,
  waiting,
  onBack,
}: {
  holderName: string | null;
  /** Someone already queued to take over — depth is 1, so a second waiter would be refused. */
  waiting: ManualLockHolder | null;
  onBack: () => void;
}) {
  const { t } = useTranslation();
  return (
    <div className="bg-background flex min-h-dvh flex-col items-center justify-center gap-4 p-6 text-center">
      <span className="bg-attention/10 text-attention flex size-12 items-center justify-center rounded-full">
        <Lock aria-hidden="true" className="size-6" />
      </span>
      <div className="space-y-1">
        {/* `role="alert"` — this replaces the screen the user expected, so a screen reader must announce it. */}
        <h1 role="alert" className="text-lg font-semibold">
          {holderName ? t("manualEditor.someoneNamedEditing", { name: holderName }) : t("manualEditor.someoneIsEditingThisManual")}
        </h1>
        <p className="text-muted-foreground max-w-prose text-sm">
          {t("manualEditor.lockExplain")}
        </p>
        {waiting ? (
          <p className="text-muted-foreground text-sm">
            {t("manualEditor.waitingNext", { name: waiting.name ?? t("manualEditor.someone") })}
          </p>
        ) : null}
      </div>
      <Button variant="outline" onPress={onBack} className="gap-1.5">
        <ChevronLeft aria-hidden="true" className="size-4" />
        {t("facilitySelection.nav.back")}
      </Button>
    </div>
  );
}
